import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { updatePreset, definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { border } from '../../assets/theme/primitive/border';
import {
  surface,
  gray,
  highlight,
  focus,
  formField,
  text,
  anchor,
  overlay,
  list,
  mask,
} from '../../assets/theme/semantic';
import {
  badge,
  button,
  datatable,
  datepicker,
  multiselect,
  tabs,
  card,
  autocomplete,
  accordion,
  tree,
  treeselect,
  tag,
} from '../../assets/theme/components';

/**
 * Service to inject tenant colors into PrimeNG theme at runtime
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeInjectorService {
  private rendererFactory = inject(RendererFactory2);
  private document = inject(DOCUMENT);
  private renderer: Renderer2;
  private styleElement: HTMLStyleElement | null = null;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }
  /**
   * Creates a dynamic theme with primary colors from API
   * @param primaryColor - The primary color from API (e.g., '#45A617')
   * @returns A PrimeNG theme preset
   */
  createDynamicTheme(primaryColor: string) {
    // Generate color scale from the primary color
    // For simplicity, we'll use the same color for all shades
    // You can enhance this to generate proper shades if needed
    const primaryColorScale = {
      50: primaryColor,
      100: primaryColor,
      200: primaryColor,
      300: primaryColor,
      400: primaryColor,
      500: primaryColor, // Main color
      600: primaryColor,
      700: primaryColor,
      800: primaryColor,
      900: primaryColor,
      950: primaryColor,
    };

    const primaryLight = {
      primary: {
        color: primaryColor,
        inverseColor: '#ffffff',
        hoverColor: primaryColor,
        activeColor: primaryColor,
      },
    };

    return definePreset(Aura, {
      primitive: {
        ...border,
      },
      semantic: {
        primary: primaryColorScale,
        colorScheme: {
          light: {
            ...primaryLight,
            ...surface,
            ...gray,
            ...highlight,
            ...focus,
            ...formField,
            ...text,
            ...anchor,
            ...overlay,
            ...list,
            ...mask,
          },
        },
      },
      components: {
        ...button,
        ...multiselect,
        ...tabs,
        ...datatable,
        ...datepicker,
        ...badge,
        ...card,
        ...autocomplete,
        ...accordion,
        ...tree,
        ...treeselect,
        ...tag,
      },
    });
  }

  /**
   * Updates the PrimeNG theme with tenant colors
   * @param primaryColor - The primary color from API
   */
  updateThemeWithTenantColors(primaryColor?: string): void {
    if (!primaryColor) {
      return;
    }

    const dynamicTheme = this.createDynamicTheme(primaryColor);
    updatePreset(dynamicTheme);
  }

  /**
   * Injects CSS variables from tenant config into the theme system
   * Uses a style element instead of direct DOM manipulation
   * @param colors - Object with CSS variable names as keys and values
   */
  injectTenantCSSVariables(colors: { [key: string]: string }): void {
    // Remove existing style element if it exists
    if (this.styleElement && this.document.head) {
      this.renderer.removeChild(this.document.head, this.styleElement);
      this.styleElement = null;
    }

    if (!colors || Object.keys(colors).length === 0 || !this.document.head) {
      return;
    }

    // Create a style element with all CSS variables
    this.styleElement = this.renderer.createElement('style');
    if (this.styleElement) {
      this.styleElement.setAttribute('id', 'tenant-theme-variables');

      // Build CSS rules
      const cssRules = Object.entries(colors)
        .filter(([_, value]) => value) // Filter out empty values
        .map(([key, value]) => `  ${key}: ${value};`)
        .join('\n');

      const cssText = `:root {\n${cssRules}\n}`;
      this.styleElement.textContent = cssText;

      // Append to head
      this.renderer.appendChild(this.document.head, this.styleElement);
    }
  }
}
